import type { AppRole } from "../types";

/**
 * Almikia Epic E5 (TASK-035): which document controls a role sees on an
 * operation. Mirrors the TASK-033 RLS matrix for display only; the server
 * stays authoritative (a lead_manager is only offered the controls because
 * the operations she can open are already limited to her scope).
 */
export type OperationDocumentControls = {
  canUpload: boolean;
  canReview: boolean;
  /** Validar / Observar / Rechazar. Never offered to the operation's advisor. */
  canValidate: boolean;
  canDelete: boolean;
  /** The advisor can upload but is told an administration role validates. */
  showAdvisorHint: boolean;
};

const UPLOAD_ROLES: AppRole[] = [
  "direccion",
  "administracion",
  "compliance",
  "lead_manager",
];
const REVIEW_ROLES: AppRole[] = ["direccion", "administracion", "lead_manager"];
const VALIDATE_ROLES: AppRole[] = [
  "direccion",
  "administracion",
  "compliance",
  "lead_manager",
];

/** Roles that read the audit log (`audit_log_select`). */
export const AUDIT_HISTORY_ROLES: AppRole[] = [
  "direccion",
  "auditoria",
  "compliance",
  "administracion",
];

/** `isAdvisor` is true when the viewer is the operation's advisor. */
export const getOperationDocumentControls = (
  role: AppRole,
  isAdvisor: boolean,
): OperationDocumentControls => {
  const isOwnerRole = role === "asesora" || role === "lider";
  const canUpload = UPLOAD_ROLES.includes(role) || (isOwnerRole && isAdvisor);
  return {
    canUpload,
    canReview: REVIEW_ROLES.includes(role),
    canValidate: VALIDATE_ROLES.includes(role) && !isAdvisor,
    canDelete: REVIEW_ROLES.includes(role),
    showAdvisorHint: canUpload && isAdvisor,
  };
};

/** Administration roles that see the payable requirement hint (besides the operation's advisor). */
const PAYABLE_HINT_ROLES: AppRole[] = [
  "direccion",
  "administracion",
  "compliance",
  "lead_manager",
];

/** Almikia Epic E6 (TASK-041): who sees the neutral "payable requirements" hint. */
export const canSeePayableHint = (role: AppRole, isAdvisor: boolean): boolean =>
  isAdvisor || PAYABLE_HINT_ROLES.includes(role);

/**
 * The two requirements of the advisor's payable: a green file and a
 * non-deleted "Factura de la asesora". Information only, never an amount.
 */
export const getPayableRequirements = (
  greenFile: boolean,
  documents: ReadonlyArray<{ doc_type: string; deleted_at?: string | null }>,
): { greenFile: boolean; invoiceUploaded: boolean } => ({
  greenFile,
  invoiceUploaded: documents.some(
    (doc) => doc.doc_type === "factura_asesora" && !doc.deleted_at,
  ),
});

export const canSeeAuditHistory = (role: AppRole): boolean =>
  AUDIT_HISTORY_ROLES.includes(role);
